import { Provider, Strategy, StrategyProviders } from "./strategies";

export interface VADConfigDraft {
  enabled: boolean;
  confidence: number;
  start_secs: number;
  stop_secs: number;
  min_volume: number;
}

export interface TurnConfigDraft {
  stop_secs: number;
  pre_speech_ms: number;
}

export interface ProviderSettingsDraft {
  provider: Provider | null;
  settings: Record<string, any>;
}

export interface RealtimeConfigDraft {
  vad: VADConfigDraft | null;
  turn: TurnConfigDraft;
  turn_detection_enabled: boolean;
  noise_reduction_enabled: boolean;
}

export interface StrategyRealtimeConfigDraft {
  realtime: RealtimeConfigDraft | null;
}

export interface STTLLMTTSStrategyConfigDraft extends StrategyRealtimeConfigDraft {
  stt: ProviderSettingsDraft | null;
  llm: ProviderSettingsDraft | null;
  tts: ProviderSettingsDraft | null;
  user_idle_timeout: number;
}

export interface LLMTTSStrategyConfigDraft extends StrategyRealtimeConfigDraft {
  llm: ProviderSettingsDraft | null;
  tts: ProviderSettingsDraft | null;
}

export interface Voice2VoiceStrategyConfigDraft extends StrategyRealtimeConfigDraft {
  model: string;
  temperature?: number;
  voice_config?: Record<string, any> | null;
}

export type StrategyConfigDraft =
  | STTLLMTTSStrategyConfigDraft
  | LLMTTSStrategyConfigDraft
  | Voice2VoiceStrategyConfigDraft
  | null;

export const DEFAULT_VAD_CONFIG: VADConfigDraft = {
  enabled: true,
  confidence: 0.6,
  start_secs: 0.1,
  stop_secs: 0.4,
  min_volume: 0.4,
};

export const DEFAULT_PROVIDER_SETTINGS: ProviderSettingsDraft = {
  provider: null,
  settings: {},
};

export const DEFAULT_REALTIME_CONFIG: RealtimeConfigDraft = {
  vad: { ...DEFAULT_VAD_CONFIG },
  turn: {
    stop_secs: 0.3,
    pre_speech_ms: 180,
  },
  turn_detection_enabled: true,
  noise_reduction_enabled: false,
};

export const DEFAULT_STT_LLM_TTS_CONFIG: STTLLMTTSStrategyConfigDraft = {
  realtime: { ...DEFAULT_REALTIME_CONFIG },
  stt: null,
  llm: {
    provider: Provider.PAYTM_INFERENCE,
    settings: {
      model: "moonshotai.kimi-k2.5",
      temperature: 0.7,
    },
  },
  tts: null,
  user_idle_timeout: 20,
};

export const DEFAULT_LLM_TTS_CONFIG: LLMTTSStrategyConfigDraft = {
  realtime: { ...DEFAULT_REALTIME_CONFIG },
  llm: {
    provider: Provider.PAYTM_INFERENCE,
    settings: {
      model: "moonshotai.kimi-k2.5",
      temperature: 0.7,
    },
  },
  tts: null,
};

export const DEFAULT_VOICE2VOICE_CONFIG: Voice2VoiceStrategyConfigDraft = {
  realtime: { ...DEFAULT_REALTIME_CONFIG },
  model: "gemini-live-2.5-flash",
  temperature: 0.7,
  voice_config: null,
};

export const cloneStrategyConfig = <T>(config: T): T =>
  JSON.parse(JSON.stringify(config));

export const emptyProviderSettings = (): ProviderSettingsDraft => ({
  provider: null,
  settings: {},
});

export const defaultProvidersState = (): StrategyProviders => ({
  v2v: null,
  llm: null,
  tts: null,
  stt: null,
});

export const defaultProvidersForStrategy = (
  strategy: Strategy | null
): StrategyProviders => {
  if (strategy === Strategy.STT_LLM_TTS) {
    return {
      v2v: null,
      llm: Provider.PAYTM_INFERENCE,
      tts: Provider.CARTESIA,
      stt: Provider.DEEPGRAM,
    };
  }

  if (strategy === Strategy.LLM_TTS) {
    return {
      v2v: null,
      llm: Provider.PAYTM_INFERENCE,
      tts: Provider.ELEVENLABS,
      stt: null,
    };
  }

  if (strategy === Strategy.VOICE2VOICE) {
    return {
      v2v: Provider.VERTEXAI,
      llm: null,
      tts: null,
      stt: null,
    };
  }

  return defaultProvidersState();
};
